import { z } from 'zod';

import type { FleetDetailFE, FleetFE, FleetVehicleRefFE } from '../systemAdmin';
import type { MsFleetDetailTO, MsFleetTeamInfo, MsFleetTO, MsVehicleInfo } from './msWireSpec';
import { emptyObjectToArray, makeSanitizeWire, safeOptional, toNamedRefs } from './restMapping';

/**
 * REST -> domain mapping for fleets, served by the management service's `FleetsApi`:
 * `GET /api/v1/fleets` returns `FleetTO[]` -> {@link fleetsFromRestV1}, `GET /api/v1/fleets/{id}`
 * returns `FleetDetailTO` -> {@link fleetDetailFromRestV1}. `V1` is the REST api version.
 */

const label = (field: string) => `fleetFromRestV1.${field}`;

/** `createdAt` / `updatedAt` — camelCase, which is why the shared `sanitizeWire` is not used. */
const FLEET_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeFleetWire = makeSanitizeWire(FLEET_DATE_KEYS);

const WireFleetTeamObjectSchema = z.object({
    id: safeOptional(z.string(), label('teams[].id')),
    name: safeOptional(z.string(), label('teams[].name')),
});

const WireFleetObjectSchema = z.object({
    id: z.string().min(1),
    name: safeOptional(z.string(), label('name')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
    teams: safeOptional(z.array(WireFleetTeamObjectSchema), label('teams')),
    numberOfVehicles: safeOptional(z.number(), label('numberOfVehicles')),
});

/**
 * `vin` is absent from the generated `VehicleInfo` but required by the api repo's
 * `SystemFleetDetailSchema`, and staging serves fleet detail today. Both keys are accepted and
 * `vin` wins, so neither shape can regress.
 */
const WireFleetVehicleObjectSchema = z.object({
    vin: safeOptional(z.string(), label('vehicles[].vin')),
    id: safeOptional(z.string(), label('vehicles[].id')),
    name: safeOptional(z.string(), label('vehicles[].name')),
});

/** `vehicles: {}` is what staging serves for a fleet with no vehicles — see `emptyObjectToArray`. */
const WireFleetDetailObjectSchema = z.object({
    fleetId: z.string().min(1),
    vehicles: safeOptional(z.preprocess(emptyObjectToArray, z.array(WireFleetVehicleObjectSchema)), label('vehicles')),
});

/** Wire schemas for `FleetTO` / `FleetDetailTO`, exported for `mocks/tests/restContract.test.ts`. */
export const WireFleetSchema = z.preprocess(sanitizeFleetWire, WireFleetObjectSchema);
export const WireFleetDetailSchema = z.preprocess(sanitizeFleetWire, WireFleetDetailObjectSchema);

type WireFleetObject = z.infer<typeof WireFleetObjectSchema>;
type WireFleetTeamObject = z.infer<typeof WireFleetTeamObjectSchema>;
type WireFleetDetailObject = z.infer<typeof WireFleetDetailObjectSchema>;
type WireFleetVehicleObject = z.infer<typeof WireFleetVehicleObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type FleetWireKeysExistOnSpec = {
    fleet: KeysExistOn<WireFleetObject, MsFleetTO>;
    fleetTeam: KeysExistOn<WireFleetTeamObject, MsFleetTeamInfo>;
    fleetDetail: KeysExistOn<WireFleetDetailObject, MsFleetDetailTO>;
    // `vin` is scoped out of the guard on purpose — see the schema comment above.
    fleetVehicle: Exclude<Exclude<keyof WireFleetVehicleObject, 'vin'>, keyof MsVehicleInfo> extends never
        ? true
        : never;
};

export const fleetFromRestV1 = (raw: unknown): FleetFE => {
    const wire = WireFleetSchema.parse(raw);
    return {
        id: wire.id,
        name: wire.name ?? '',
        numberOfVehicles: wire.numberOfVehicles ?? 0,
        teams: toNamedRefs(wire.teams, label('teams[].id')),
        createdAt: wire.createdAt ?? '',
    };
};

export const fleetsFromRestV1 = (raw: unknown): FleetFE[] => {
    if (!Array.isArray(raw)) {
        console.error(`Zod catch triggered (${label('list')})`, raw);
        return [];
    }
    return raw.reduce<FleetFE[]>((acc, entry) => {
        try {
            acc.push(fleetFromRestV1(entry));
        } catch (error) {
            console.error(`Zod catch triggered (${label('list[]')})`, entry, error);
        }
        return acc;
    }, []);
};

export const fleetDetailFromRestV1 = (raw: unknown): FleetDetailFE => {
    const wire = WireFleetDetailSchema.parse(raw);
    // An empty vin would collide as the `SelectVehiclesTable` rowKey, so unidentifiable entries go.
    const vehicles = (wire.vehicles ?? []).reduce<FleetVehicleRefFE[]>((acc, entry) => {
        const vin = entry.vin ?? entry.id;
        if (!vin) {
            console.error(`Zod catch triggered (${label('vehicles[].vin')})`, entry);
            return acc;
        }
        acc.push({ vin, name: entry.name ?? '' });
        return acc;
    }, []);
    return { fleetId: wire.fleetId, vehicles };
};
